<template>
  <div class="md-layout">
    <CSLoading :is-loading="isLoading"></CSLoading>
    <ImageZoom :url="zoomUrl" @close="zoomUrl = null" />
    <div class="md-layout-item md-size-100 procurement-list">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">add_shopping_cart</span>
          </div>
          <h4 class="title">{{ $t("layout.procurement.purchase") }}</h4>
        </md-card-header>
        <md-card-content>
          <div class="table-sticky">
            <v-server-table :columns="tableColumns" :options="tableOptions" ref="table" class="table-procurement" id="table-procurement">
              <template #h__select>
                <md-checkbox class="row-select-checkbox" v-model="allOnPageSelected"></md-checkbox>
              </template>
              <template #select="{ row }">
                <!-- :key erzwingt eine eigene Checkbox je Produkt. Ohne den Schluessel werden die
                     Instanzen beim Filtern wiederverwendet und behalten ihren alten Haken. -->
                <md-checkbox :key="'cart-' + row.productId" class="row-select-checkbox" v-model="row.checked" @change="toggleCart(row, row.checked)"></md-checkbox>
              </template>
              <template v-for="g in sortGroups" #[`h__${g.column}`]>
                <span :key="g.column" @click.stop>
                  <SortMenu :label="$t('procurement.table.' + g.label)" :options="g.options" :order="menuOrder" @sort="(field, asc) => sortGroup(g.column, field, asc)" />
                </span>
              </template>
              <template #oqs="{ row }">
                <oqs-cell :row="row" />
              </template>
              <template #image="{ row }">
                <ProductImage :sku="row.sku" :size="56" @zoom="zoomUrl = $event" />
              </template>
              <!-- Eine Spalte, zwei Sortierungen: SKU alphabetisch oder OCC-Produkt-ID (neue Artikel zuerst) -->
              <template #h__sku>
                <span class="sku-head">
                  <a :class="{ active: skuSortField === 'sku' }" @click.stop="sortSkuBy('sku')">{{ $t("procurement.table.sku") }}</a>
                  /
                  <a :class="{ active: skuSortField === 'productId' }" @click.stop="sortSkuBy('productId')">#ID</a>
                </span>
              </template>
              <template #sku="{ row }">
                <div class="cell-sku">
                  <div class="text-nowrap">
                    <b>{{ row.sku }}</b>
                  </div>
                  <div class="cell-idline">#{{ row.productId }}</div>
                  <!-- nach rechts oeffnen: die SKU-Spalte ist ganz links, lange Produktnamen
                       liefen mit direction=top aus dem Fenster -->
                  <md-tooltip md-direction="right">{{ row.name }}</md-tooltip>
                </div>
              </template>
              <template #stock="{ row }">
                <div class="cell-grouped">
                  <!-- Mouseover: Mengen je Lagerplatz bzw. je Container mit Zustelldatum (Trello evD8Gwb4 Punkte 15/16) -->
                  <div class="text-nowrap">
                    {{ $t("procurement.table.inStock") }}:
                    <inventory-detail-tooltip
                      :rows="stockRows(row)"
                      :value="row.inStock || 0"
                      :is-loading="!!stockLoading[row.sku]"
                      :can-open="(row.inStock || 0) > 0"
                      @open="loadStock(row.sku)"
                    >
                      {{ row.inStock }}
                    </inventory-detail-tooltip>
                  </div>
                  <div class="text-nowrap" @mouseenter="loadInflow(row)">
                    {{ $t("procurement.table.inFlow") }}:
                    <span class="inflow-value" :class="{ 'inflow-value--active': row.inFlow }">
                      {{ row.inFlow }}
                      <md-tooltip v-if="row.inFlow" md-direction="top" class="inflow-tooltip">
                        <div v-if="!inflows[row.productId]">{{ $t("products.inflow.loading") }}</div>
                        <div v-else-if="!inflows[row.productId].length">{{ $t("products.inflow.none") }}</div>
                        <table v-else class="inflow-table">
                          <thead>
                            <tr>
                              <th>{{ $t("products.inflow.container") }}</th>
                              <th>{{ $t("products.inflow.arrival") }}</th>
                              <th>{{ $t("products.inflow.quantity") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="i in inflows[row.productId]" :key="i.shipmentRecordId">
                              <td :title="i.shipmentRecordName">{{ i.containerId || "–" }}</td>
                              <td>{{ i.arrivalDate ? formatDate(i.arrivalDate) : "–" }}</td>
                              <td class="text-right">{{ i.quantity }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </md-tooltip>
                    </span>
                  </div>
                  <div class="text-nowrap">
                    <b>{{ $t("procurement.table.totalStock") }}: {{ row.totalStock }}</b>
                  </div>
                </div>
              </template>
              <template #sales="{ row }">
                <!-- Summe aus Lagerabsatz + FBA; Aufteilung im Tooltip -->
                <div class="cell-grouped">
                  <div class="text-nowrap" :title="salesTooltip(row, 7)">7d: {{ row.sales7 + row.fba7 }}</div>
                  <div class="text-nowrap" :title="salesTooltip(row, 30)">30d: {{ row.sales30 + row.fba30 }}</div>
                  <div class="text-nowrap" :title="salesTooltip(row, 90)">90d: {{ row.sales90 + row.fba90 }}</div>
                  <div class="text-nowrap" :title="salesTooltip(row, 365)">365d: {{ row.sales365 + row.fba365 }}</div>
                </div>
              </template>
              <template #chart="{ row }">
                <SalesSparkline :series="row.monthlySales" @click="showChartModal(row)" />
              </template>
              <template #range="{ row }">
                <div class="cell-grouped">
                  <div class="text-nowrap">30d: {{ formatRange(row.range30) }}</div>
                  <div class="text-nowrap">90d: {{ formatRange(row.range90) }}</div>
                  <div class="text-nowrap">365d: {{ formatRange(row.range365) }}</div>
                </div>
              </template>
              <template #moq="{ row }">
                <div class="cell-moq">
                  <div class="moq-eol-info">
                    <div class="text-nowrap">MOQ: {{ row.moq != null ? row.moq : "–" }}</div>
                    <div class="text-nowrap eol-indicator">
                      EOL:
                      <md-icon v-if="row.eol" class="eol-yes">check</md-icon>
                      <md-icon v-else class="eol-no">close</md-icon>
                    </div>
                  </div>
                  <md-button
                    v-if="hasPermission('Write', 'Procurement', 'EolMoq')"
                    class="md-icon-button md-simple eol-moq-edit"
                    @click="openEolMoqModal(row)"
                  >
                    <md-icon>edit</md-icon>
                  </md-button>
                </div>
              </template>
              <template #supplierName="{ row }">
                <div v-for="s in row.suppliers || []" :key="s.id">
                  <a href="javascript:void(0)" class="supplier-link" @click="filterBySupplier(s)">{{ s.name }}</a>
                </div>
              </template>
              <template #recommendation="{ row }">
                <div class="cell-recommendation">
                  <div class="text-nowrap">
                    <b>{{ row.recommendedQuantity != null ? row.recommendedQuantity : "–" }}</b>
                    <span v-if="row.recommendedCbm != null" class="cbm-info"> ({{ row.recommendedCbm | formatUnit("cbm", 2, 2) }})</span>
                  </div>
                  <div class="order-quantity-row">
                    <md-field class="order-quantity-field">
                      <md-input type="number" min="0" step="1" v-model.number="row.orderQuantity" @input="syncRowQuantity(row)"></md-input>
                    </md-field>
                    <span v-if="calcCbm(row) != null" class="cbm-info">{{ calcCbm(row) | formatUnit("cbm", 2, 2) }}</span>
                  </div>
                </div>
              </template>
              <template #margin="{ row }">
                <div class="cell-prices cell-pricemargin">
                  <table class="pm-table">
                    <tbody>
                      <tr>
                        <td class="pm-label">{{ $t("procurement.priceCell.epBep") }}</td>
                        <td class="pm-value">{{ fmtUsd(row.lastPurchasePrice) }} / {{ fmtEur(row.bep) }}</td>
                      </tr>
                      <tr>
                        <td class="pm-label">{{ $t("procurement.priceCell.grossProfit") }}</td>
                        <td class="pm-value" :class="marginSign(grossProfitEur(row))">{{ eurPct(grossProfitEur(row), grossProfitPct(row)) }}</td>
                      </tr>
                      <tr v-for="d in ['db1', 'db2', 'db3']" :key="d">
                        <td class="pm-label">{{ d.toUpperCase() }}</td>
                        <td class="pm-value" :class="marginSign(row[d])">{{ eurPct(row[d], row[d + "Percent"]) }}</td>
                      </tr>
                      <tr>
                        <td class="pm-label">{{ $t("procurement.priceCell.vk") }}</td>
                        <td class="pm-value">
                          {{ fmtEur(saleGross(row)) }} / {{ fmtEur(saleNet(row)) }}
                          <!-- Klick oeffnet die Kalkulation als Lightbox mit Angebotsauswahl (Trello evD8Gwb4 Punkt 14) -->
                          <span class="pm-info" @click="calcRow = row">
                            <md-icon>info</md-icon>
                            <md-tooltip md-direction="left">{{ $t("procurement.calcDialog.open") }}</md-tooltip>
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </v-server-table>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Sticky-Dock rechts: Bestellen, Suche, Filter (wie im Lieferplan) -->
    <div class="sp-dock">
      <a
        v-if="hasPermission('Write', 'Procurement', 'Order')"
        class="sp-dock-trigger"
        :class="{ 'sp-dock-trigger--disabled': selectedCount === 0 }"
        :title="$t('procurement.order.button')"
        @click="onOrderClick"
      >
        <md-icon>shopping_cart</md-icon>
        <span v-if="selectedCount" class="sp-dock-badge">{{ selectedCount }}</span>
      </a>
      <div class="sp-dock-search">
        <a class="sp-dock-trigger" :class="{ 'sp-dock-trigger--active': filters.keywords || filters.sku }" @click="toggleDockSearch">
          <md-icon>search</md-icon>
        </a>
        <div v-show="dockSearchOpen" class="sp-dock-search-panel">
          <md-field md-clearable>
            <label>{{ $t("procurement.filters.keywords") }}</label>
            <md-input ref="dockSearchInput" v-model="filters.keywords" @input="debounceApplyFilters"></md-input>
          </md-field>
          <md-field md-clearable>
            <label>{{ $t("procurement.filters.sku") }}</label>
            <md-input v-model="filters.sku" @input="debounceApplyFilters"></md-input>
          </md-field>
          <md-switch v-model="filters.exactMatchSku" @change="debounceApplyFilters">{{ $t("procurement.filters.exactMatch") }}</md-switch>
        </div>
      </div>
      <ProcurementFilterDrawer
        :supplier="supplierFilter"
        :kpi="kpi"
        :kpi-fields="kpiFields"
        :presets="kpiPresets"
        :supplier-options="supplierNameOptions"
        :filter-supplier="filterSupplier"
        @apply-filters="onDrawerFilters"
      />
    </div>

    <!-- Sticky-Zusammenfassung unten rechts: EOL-Schalter + Warenkorb + Auswahl-Kennzahlen -->
    <div class="sp-dock-info procurement-dock-info" :class="{ 'sp-dock-info--collapsed': summaryCollapsed }">
      <!-- Einklappbar auf Kundenwunsch (Trello 20.08.): Pfeil oben rechts, Standard offen -->
      <a class="sp-dock-info-toggle" :title="$t(summaryCollapsed ? 'procurement.summary.expand' : 'procurement.summary.collapse')" @click="summaryCollapsed = !summaryCollapsed">
        <md-icon>{{ summaryCollapsed ? "expand_less" : "expand_more" }}</md-icon>
      </a>
      <template v-if="!summaryCollapsed">
        <md-switch v-model="filters.includeEol" @change="debounceApplyFilters" class="md-primary">{{ $t("procurement.filters.includeEol") }}</md-switch>

        <!-- Warenkorb-Positionen: entfernen geht sonst nur ueber die Zeile, die der Filter ausblenden kann -->
        <template v-if="cartOpen && selectedCount">
          <div class="cart-head">
            <strong>{{ $t("procurement.cart.title") }} · {{ selectedCount }}</strong>
            <a class="cart-clear" @click="clearCart">{{ $t("procurement.cart.clearAll") }}</a>
          </div>
          <div class="cart-list">
            <div v-for="item in cartItems" :key="item.productId" class="cart-row" :class="{ 'cart-row--foreign': isForeignSupplier(item) }">
              <span class="cart-sku" :title="item.supplierName">
                {{ item.sku }}
                <md-icon v-if="isForeignSupplier(item)" class="cart-warn-icon">warning</md-icon>
              </span>
              <input type="number" min="0" step="1" class="cart-qty" :value="item.orderQuantity" @input="updateCartQuantity(item, $event.target.value)" />
              <span class="cart-cbm">{{ calcCbm(item) | formatUnit("cbm", 2, 2) }}</span>
              <a class="cart-remove" :title="$t('procurement.cart.remove')" @click="removeFromCart(item)"><md-icon>close</md-icon></a>
            </div>
          </div>
          <div v-if="commonSuppliers.length === 0" class="cart-hint">
            <md-icon>warning</md-icon>
            <span>{{ $t("procurement.cart.multiSupplier") }}</span>
          </div>
        </template>

        <div class="sp-dock-info-row">
          <span>{{ $t("procurement.summary.sku") }}</span>
          <strong>{{ selectedCount }}</strong>
        </div>
        <div class="sp-dock-info-row">
          <span>{{ $t("procurement.summary.cbm") }}</span>
          <strong>{{ selectedCbm | formatUnit("cbm", 2, 2) }}</strong>
        </div>
        <div class="sp-dock-info-row">
          <span>{{ $t("procurement.summary.container") }} (40 HC)</span>
          <strong>{{ container40hc | formatUnit("", 2, 2) }}</strong>
        </div>
        <div class="sp-dock-info-row">
          <span>{{ $t("procurement.summary.container") }} (40 GP)</span>
          <strong>{{ container40gp | formatUnit("", 2, 2) }}</strong>
        </div>
        <div class="sp-dock-info-row">
          <span>{{ $t("procurement.summary.container") }} (20 GP)</span>
          <strong>{{ container20gp | formatUnit("", 2, 2) }}</strong>
        </div>
        <a v-if="selectedCount" class="cart-toggle" @click="cartOpen = !cartOpen">
          <md-icon>{{ cartOpen ? "expand_more" : "expand_less" }}</md-icon>
          <span>{{ cartOpen ? $t("procurement.cart.hide") : $t("procurement.cart.show") }}</span>
        </a>
      </template>
    </div>

    <!-- Chart-Lightbox -->
    <procurement-calc-dialog v-if="calcRow" :row="calcRow" :calc-cache="calcCache" @close="calcRow = null" />
    <modal v-if="chartModal" @close="chartModal = false" class-name="procurement-chart-modal">
      <template slot="header">
        <h4 class="modal-title">{{ chartRow && chartRow.sku }} – {{ $t("procurement.chart.title") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="chartModal = false"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <div class="procurement-chart-wrap">
          <div v-if="chartRow" class="chart-legend">
            <span class="legend-item"><span class="legend-dot" style="background: #7f915a"></span>{{ $t("procurement.salesSplit.warehouse") }}</span>
            <span class="legend-item"><span class="legend-dot" style="background: #ff9800"></span>FBA</span>
          </div>
          <svg v-if="chartRow" :width="chartWidth" :height="chartHeight" class="big-chart">
            <g v-for="(bar, i) in bigChartBars" :key="i">
              <title>{{ bar.label }}: {{ bar.tooltip }}</title>
              <rect v-if="bar.hLager > 0" :x="bar.x" :y="bar.yLager" :width="bar.w" :height="bar.hLager" rx="2" fill="#7f915a"></rect>
              <rect v-if="bar.hFba > 0" :x="bar.x" :y="bar.yFba" :width="bar.w" :height="bar.hFba" rx="2" fill="#ff9800"></rect>
              <rect v-if="bar.total === 0" :x="bar.x" :y="yBase - 2" :width="bar.w" height="2" rx="1" fill="#e0e0e0"></rect>
              <text :x="bar.x + bar.w / 2" :y="bar.yTop - 6" text-anchor="middle" class="bar-value">{{ bar.total }}</text>
              <text :x="bar.x + bar.w / 2" :y="chartHeight - 6" text-anchor="middle" class="bar-label">{{ bar.label }}</text>
            </g>
          </svg>
        </div>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="chartModal = false">{{ $t("procurement.close") }}</md-button>
      </template>
    </modal>

    <!-- EOL/MOQ-Lightbox: schreibt direkt in Products -->
    <modal v-if="eolMoqModal" @close="closeEolMoqModal" class-name="procurement-eolmoq-modal">
      <template slot="header">
        <h4 class="modal-title">{{ eolMoqRow && eolMoqRow.sku }} – {{ $t("procurement.eolMoq.title") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="closeEolMoqModal"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <div class="eolmoq-form">
          <md-field>
            <label>{{ $t("procurement.eolMoq.moq") }}</label>
            <md-input v-model="eolMoqForm.moq" type="number" min="0" step="1"></md-input>
          </md-field>
          <md-switch v-model="eolMoqForm.eol" class="md-primary">{{ $t("procurement.eolMoq.eol") }}</md-switch>
        </div>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="closeEolMoqModal">{{ $t("procurement.cancel") }}</md-button>
        <md-button class="md-success" :disabled="eolMoqSaving" @click="saveEolMoq">{{ $t("procurement.save") }}</md-button>
      </template>
    </modal>

    <!-- Hinweis: mehrere Lieferanten -->
    <modal v-if="multiSupplierModal" @close="multiSupplierModal = false">
      <template slot="header">
        <h4 class="modal-title">{{ $t("procurement.order.multiSupplierTitle") }}</h4>
      </template>
      <template slot="body">
        <p>{{ $t("procurement.order.multiSupplierText") }}</p>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="multiSupplierModal = false">{{ $t("procurement.close") }}</md-button>
      </template>
    </modal>

    <!-- Bestell-Bestätigung -->
    <modal v-if="confirmModal" @close="confirmModal = false">
      <template slot="header">
        <h4 class="modal-title">{{ $t("procurement.order.confirmTitle") }}</h4>
      </template>
      <template slot="body">
        <p>{{ $t("procurement.order.selectSupplier") }}</p>
        <SelectSupplier v-model="orderSupplierId" :options="commonSuppliers" :clearable="false" :append-to-body="false" custom-class="custom-select order-supplier-select" />
        <div class="order-options">
          <md-checkbox v-model="orderCreatePlan">{{ $t("procurement.order.createShippingPlan") }}</md-checkbox>
          <md-checkbox v-model="orderSendEmail">{{ $t("procurement.order.sendEmail") }}</md-checkbox>
        </div>
        <div v-if="orderCreatePlan" class="order-date">
          <p class="order-date-title">{{ $t("procurement.order.arrivalDate") }}</p>
          <DateTimePicker :startDate="orderArrivalDate" :singleDate="true" @onChange="(d) => (orderArrivalDate = d.startDate)" class="datetime-input" timeFormat="HH:mm" />
        </div>
        <p v-if="orderSendEmail && !orderSupplierEmail" class="text-danger">{{ $t("procurement.order.noSupplierEmail") }}</p>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="confirmModal = false">{{ $t("procurement.cancel") }}</md-button>
        <md-button
          class="md-success"
          v-if="hasPermission('Write', 'Procurement', 'Order')"
          :disabled="!orderSupplier || (!orderCreatePlan && !orderSendEmail) || (orderSendEmail && !orderSupplierEmail)"
          @click="confirmOrder"
        >
          {{ $t("procurement.confirm") }}
        </md-button>
      </template>
    </modal>

    <!-- E-Mail-Editor -->
    <modal v-if="emailModal" @close="emailModal = false" class-name="procurement-email-modal">
      <template slot="header">
        <h4 class="modal-title">{{ $t("procurement.email.title", { supplier: orderSupplierName }) }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="emailModal = false"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <md-field>
          <label>{{ $t("procurement.email.to") }}</label>
          <md-input :value="orderSupplierEmail" readonly></md-input>
        </md-field>
        <md-field>
          <label>{{ $t("procurement.email.subject") }}</label>
          <md-input v-model="emailSubject"></md-input>
        </md-field>
        <md-field>
          <label>{{ $t("procurement.email.body") }}</label>
          <md-textarea v-model="emailBody" class="email-body-input"></md-textarea>
        </md-field>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="emailModal = false">{{ $t("procurement.cancel") }}</md-button>
        <md-button class="md-success" v-if="hasPermission('Write', 'Procurement', 'Order')" @click="submitOrder">{{ $t("procurement.email.send") }}</md-button>
      </template>
    </modal>
  </div>
</template>
<script>
import OqsCell from "@/components/Procurement/OqsCell.vue";
import InventoryDetailTooltip from "@/components/Wms/InventoryDetailTooltip.vue";
import ProcurementFilterDrawer from "./ProcurementFilterDrawer.vue";
import ProcurementCalcDialog from "@/components/Procurement/ProcurementCalcDialog.vue";
import SortMenu from "../Products/SortMenu.vue";

// Kennzahlfilter wie ProcurementKpiFields in der API; decimals = erlaubte Nachkommastellen im Eingabefeld
const KPI_KEYS = [
  { key: "oqs", decimals: 1, unit: "none" },
  { key: "inStock", decimals: 0, unit: "pcs" },
  { key: "inFlow", decimals: 0, unit: "pcs" },
  { key: "totalStock", decimals: 0, unit: "pcs" },
  { key: "sales30", decimals: 0, unit: "pcs" },
  { key: "sales90", decimals: 0, unit: "pcs" },
  { key: "sales365", decimals: 0, unit: "pcs" },
  { key: "range30", decimals: 1, unit: "months" },
  { key: "range90", decimals: 1, unit: "months" },
  { key: "range365", decimals: 1, unit: "months" },
  { key: "moq", decimals: 0, unit: "pcs" },
  { key: "recommendedQuantity", decimals: 0, unit: "pcs" },
  { key: "lastPurchasePrice", decimals: 2, unit: "none" },
  { key: "bep", decimals: 2, unit: "eur" },
  { key: "grossProfit", decimals: 2, unit: "eur" },
  { key: "grossProfitPercent", decimals: 1, unit: "percent" },
  { key: "db1", decimals: 2, unit: "eur" },
  { key: "db1Percent", decimals: 1, unit: "percent" },
  { key: "db2", decimals: 2, unit: "eur" },
  { key: "db2Percent", decimals: 1, unit: "percent" },
  { key: "db3", decimals: 2, unit: "eur" },
  { key: "db3Percent", decimals: 1, unit: "percent" },
  { key: "currentSellingPrice", decimals: 2, unit: "eur" },
];
const emptyKpi = () => Object.fromEntries(KPI_KEYS.map((k) => [k.key, { op: "", value: "" }]));
import { debounce } from "lodash";
import { formatISO } from "date-fns";
import { DateTimePicker } from "@lazy-copilot/datetimepicker";
import { Modal } from "@/components";
import axiosFactory from "../../services/axios.factory";
import CSLoading from "../../components/Loading/CSLoading.vue";
import { EventBus } from "@/helpers/event-bus";
import SalesSparkline from "./SalesSparkline.vue";
import ProductImage from "@/components/Products/ProductImage.vue";
import ImageZoom from "@/components/Products/ImageZoom.vue";
import SelectSupplier from "@/components/SelectSupplier/SelectSupplier.vue";
import { hasPermission } from "../../mixins/auth";

export default {
  name: "Procurement",
  components: {
    OqsCell,
    InventoryDetailTooltip,
    ProcurementFilterDrawer,
    ProcurementCalcDialog,
    SortMenu,
    ProductImage,
    ImageZoom,
    Modal,
    CSLoading,
    SalesSparkline,
    SelectSupplier,
    DateTimePicker,
  },
  data() {
    return {
      isLoading: true,
      stockDetails: {},
      kpi: emptyKpi(),
      groupSort: { stock: "totalStock", sales: "sales365", range: "range365", margin: "db1Percent" },
      menuOrder: null,
      calcRow: null,
      stockLoading: {},
      inflows: {},
      zoomUrl: null,
      currentPage: 1,
      filters: {
        keywords: "",
        sku: "",
        exactMatchSku: false,
        includeEol: false,
      },
      supplierFilter: null,
      calcCache: {},
      skuSortField: "productId",
      summaryCollapsed: false,
      dockSearchOpen: false,
      selected: {},
      cartOpen: false,
      chartModal: false,
      chartRow: null,
      chartWidth: 880,
      chartHeight: 400,
      multiSupplierModal: false,
      eolMoqModal: false,
      eolMoqRow: null,
      eolMoqForm: { moq: null, eol: false },
      eolMoqSaving: false,
      confirmModal: false,
      emailModal: false,
      orderCreatePlan: true,
      orderSendEmail: true,
      orderSupplierId: null,
      orderArrivalDate: null,
      emailSubject: "",
      emailBody: "",
      tableColumns: ["select", "oqs", "image", "sku", "stock", "sales", "chart", "range", "moq", "supplierName", "margin", "recommendation"],
      tableOptions: {
        headings: {
          select: () => "",
          oqs: () => this.$t("procurement.table.oqs"),
          image: () => "",
          sku: () => this.$t("procurement.table.sku"),
          stock: () => this.$t("procurement.table.stock"),
          sales: () => this.$t("procurement.table.sales"),
          chart: () => this.$t("procurement.table.chart"),
          range: () => this.$t("procurement.table.range"),
          moq: () => this.$t("procurement.table.moq"),
          supplierName: () => this.$t("procurement.table.supplier"),
          margin: () => this.$t("procurement.table.priceMargin"),
          recommendation: () => this.$t("procurement.table.recommendation"),
        },
        perPage: 25,
        perPageValues: [25, 100, 500],
        // Standard: hoechster Order Quality Score zuerst (Trello evD8Gwb4 Punkt 18)
        orderBy: { column: "oqs", ascending: false },
        filterable: false,
        sortIcon: {
          is: "fa-sort",
          base: "fa",
          up: "fa-sort-up",
          down: "fa-sort-down",
        },
        sortable: ["oqs", "sku", "moq", "supplierName", "recommendation"],
        params: {},
        resizableColumns: false,
        requestFunction: this.requestFunction,
        responseAdapter: this.responseFunction,
        pagination: {
          show: true,
        },
        texts: {
          count: this.$t("table.count"),
          limit: this.$t("table.records"),
          noResults: this.$t("table.noRecord"),
        },
        columnsClasses: {
          sku: "text-left",
          stock: "text-left",
          sales: "text-left",
          range: "text-left",
          supplierName: "text-left",
          recommendation: "text-left",
        },
      },
      tableData: [],
      totalItems: 0,
    };
  },
  computed: {
    kpiFields() {
      return KPI_KEYS.map((k) => ({ ...k, label: this.$t("procurement.kpi." + k.key) }));
    },
    activeKpis() {
      return KPI_KEYS.filter((k) => this.kpi[k.key].op && this.kpi[k.key].value !== "" && this.kpi[k.key].value != null);
    },
    kpiPresets() {
      const negative = (key, name) => ({ key, op: "lt", value: 0, label: this.$t("procurement.filters.negative", { name }) });
      return [
        { key: "oqs", op: "gt", value: 80, label: this.$t("procurement.filters.oqsGreen") },
        negative("grossProfit", this.$t("procurement.priceCell.grossProfit")),
        negative("db1", "DB1"),
        negative("db2", "DB2"),
        negative("db3", "DB3"),
      ];
    },
    sortGroups() {
      const opts = (keys) => keys.map((key) => ({ key, label: this.$t("procurement.kpi." + key) }));
      return [
        { column: "stock", label: "stock", options: opts(["inStock", "inFlow", "totalStock"]) },
        { column: "sales", label: "sales", options: opts(["sales30", "sales90", "sales365"]) },
        { column: "range", label: "range", options: opts(["range30", "range90", "range365"]) },
        {
          column: "margin",
          label: "priceMargin",
          options: opts(["lastPurchasePrice", "bep", "grossProfit", "grossProfitPercent", "db1", "db1Percent", "db2", "db2Percent", "db3", "db3Percent", "currentSellingPrice"]),
        },
      ];
    },
    supplierNameOptions() {
      return (this.$store.state.shippingData.supplierNames || []).map((s) => ({ value: s.id, name: s.name, nameFull: s.nameFull, creditor: s.creditor, bankId: s.bankId }));
    },
    selectedItems() {
      return Object.values(this.selected);
    },
    selectedCount() {
      return this.selectedItems.length;
    },
    // Summe der CBM über die ausgewählten Zeilen (jeweils mit ihrer eingegebenen Menge)
    selectedCbm() {
      return this.selectedItems.reduce((sum, item) => {
        const cbm = this.calcCbm(item);
        return sum + (cbm || 0);
      }, 0);
    },
    // Warenkorb-Positionen, stabil sortiert nach SKU
    cartItems() {
      return this.selectedItems.slice().sort((a, b) => String(a.sku || "").localeCompare(String(b.sku || "")));
    },
    // Bestellt wird immer bei genau einem Lieferanten -> Schnittmenge der Lieferanten aller
    // Positionen (Reihenfolge des ersten Artikels, dessen Hauptlieferant steht vorn)
    commonSuppliers() {
      const items = this.selectedItems;
      if (items.length === 0) return [];
      return (items[0].suppliers || []).filter((s) => items.every((i) => (i.suppliers || []).some((x) => x.id === s.id)));
    },
    orderSupplier() {
      return this.commonSuppliers.find((s) => s.id === this.orderSupplierId) || null;
    },
    // Container-Auslastung: CBM geteilt durch das PLANBARE Ladevolumen (nicht das
    // Bruttovolumen) - Werte laut Kundenvorgabe 08/2026
    container40hc() {
      return this.selectedCbm / 68;
    },
    container40gp() {
      return this.selectedCbm / 58;
    },
    container20gp() {
      return this.selectedCbm / 28;
    },
    allOnPageSelected: {
      get() {
        return this.tableData.length > 0 && this.tableData.every((r) => this.selected[r.productId]);
      },
      set(value) {
        this.tableData.forEach((r) => {
          r.checked = value;
          this.toggleCart(r, value);
        });
      },
    },
    orderSupplierName() {
      return this.orderSupplier && this.orderSupplier.name;
    },
    // Basislinie des grossen Charts (fuer die Null-Monats-Markierung)
    yBase() {
      return this.chartHeight - 30;
    },
    orderSupplierEmail() {
      return this.orderSupplier && this.orderSupplier.email;
    },
    bigChartBars() {
      if (!this.chartRow) return [];
      const series = this.chartRow.monthlySales || [];
      const n = series.length || 1;
      const padding = 20;
      const innerWidth = this.chartWidth - 2 * padding;
      const barSpace = innerWidth / n;
      const barWidth = Math.floor(barSpace * 0.6);
      // Stapel: Lager (gruen) unten + FBA (orange) oben; Skala nach dem staerksten Gesamtmonat
      const maxVal = Math.max(1, ...series.map((s) => (s.quantity || 0) + (s.quantityFba || 0)));
      const chartInner = this.chartHeight - 60;
      const baseY = this.chartHeight - 30;
      return series.map((s, i) => {
        const lager = s.quantity || 0;
        const fba = s.quantityFba || 0;
        const total = lager + fba;
        const hLager = lager > 0 ? Math.max(2, Math.round((chartInner * lager) / maxVal)) : 0;
        const hFba = fba > 0 ? Math.max(2, Math.round((chartInner * fba) / maxVal)) : 0;
        const d = new Date(s.month);
        return {
          x: padding + i * barSpace + (barSpace - barWidth) / 2,
          w: barWidth,
          yLager: baseY - hLager,
          hLager,
          yFba: baseY - hLager - hFba,
          hFba,
          yTop: baseY - Math.max(2, hLager + hFba),
          lager,
          fba,
          total,
          label: `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(2)}`,
          tooltip: `${this.$t("procurement.salesSplit.warehouse")} ${lager} · FBA ${fba} · ${this.$t("procurement.salesSplit.total")} ${total}`,
        };
      });
    },
  },
  methods: {
    hasPermission(permissionName, tableName, columnName) {
      return hasPermission(permissionName, tableName, columnName);
    },
    onOrderClick() {
      if (this.selectedCount > 0) this.startOrder();
    },
    toggleDockSearch() {
      this.dockSearchOpen = !this.dockSearchOpen;
      if (this.dockSearchOpen) {
        this.$nextTick(() => {
          const ref = this.$refs.dockSearchInput;
          const input = ref && ref.$el && ref.$el.querySelector("input");
          if (input) input.focus();
        });
      }
    },
    onDrawerFilters({ supplier, kpi }) {
      this.supplierFilter = supplier;
      this.kpi = kpi;
      this.applyFilters({});
    },
    openEolMoqModal(row) {
      this.eolMoqRow = row;
      this.eolMoqForm = { moq: row.moq != null ? row.moq : null, eol: !!row.eol };
      this.eolMoqModal = true;
    },
    closeEolMoqModal() {
      this.eolMoqModal = false;
      this.eolMoqRow = null;
    },
    saveEolMoq() {
      if (!this.eolMoqRow) return;
      const moqRaw = this.eolMoqForm.moq;
      const payload = {
        moq: moqRaw === "" || moqRaw === null || moqRaw === undefined ? null : parseInt(moqRaw, 10),
        eol: !!this.eolMoqForm.eol,
      };
      this.eolMoqSaving = true;
      axiosFactory()
        .put(`/procurement/items/${this.eolMoqRow.productId}/eol_moq`, payload)
        .then(() => {
          this.$notify({
            timeout: 2500,
            message: this.$t("procurement.eolMoq.updated"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
          this.eolMoqModal = false;
          this.eolMoqRow = null;
          if (this.$refs.table) this.$refs.table.refresh();
        })
        .catch(() => {
          this.$notify({
            timeout: 3000,
            message: this.$t("procurement.eolMoq.error"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger",
          });
        })
        .finally(() => {
          this.eolMoqSaving = false;
        });
    },
    filterSupplier(options, search) {
      const s = (search || "").toLowerCase().trim();
      if (!s) return options;
      return options.filter((o) => [o.name, o.nameFull, o.creditor, o.bankId].some((v) => v != null && String(v).toLowerCase().includes(s)));
    },
    requestFunction(data) {
      const { page, limit, orderBy, ascending } = data;
      this.currentPage = page;
      const params = {
        pageIndex: this.currentPage,
        pageSize: limit,
        orderBy,
        ascending,
        keywords: this.filters.keywords || undefined,
        includeEol: this.filters.includeEol,
        supplierId: this.supplierFilter ? this.supplierFilter.value : undefined,
      };
      if (this.filters.sku) {
        params.sku = this.filters.sku;
        params.exactMatchSku = this.filters.exactMatchSku;
      }
      // Gruppenspalten auf das im Sortiermenue gewaehlte Feld des Backends mappen
      const grouped = Object.prototype.hasOwnProperty.call(this.groupSort, orderBy);
      if (grouped) params.orderBy = this.groupSort[orderBy];
      this.menuOrder = grouped ? { column: this.groupSort[orderBy], ascending: !!ascending } : null;
      if (orderBy === "recommendation") params.orderBy = "recommendedQuantity";
      const kpiFilters = this.activeKpis.filter((k) => this.kpiValid(k)).map((k) => `${k.key}:${this.kpi[k.key].op}:${Number(this.kpi[k.key].value)}`);
      if (kpiFilters.length) params.kpiFilters = kpiFilters.join(",");
      // SKU-Spalte sortiert nach der OCC-Produkt-ID (Trello 05.09.2026)
      if (orderBy === "sku") params.orderBy = this.skuSortField;
      this.isLoading = true;
      return axiosFactory()
        .get(`/procurement/items`, { params })
        .catch(function (e) {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    responseFunction({ data }) {
      this.tableData = data.data.map((i) => ({
        ...i,
        checked: !!this.selected[i.productId],
        orderQuantity: this.selected[i.productId] ? this.selected[i.productId].orderQuantity : i.recommendedQuantity || null,
      }));
      this.totalItems = data.total;
      return {
        data: this.tableData,
        count: data.total,
      };
    },
    kpiValid(k) {
      const entry = this.kpi[k.key];
      if (!entry.op || entry.value === "" || entry.value == null) return true;
      const text = String(entry.value);
      if (!Number.isFinite(Number(text))) return false;
      const decimals = text.includes(".") ? text.split(".")[1].length : 0;
      return decimals <= k.decimals;
    },
    // Sortiermenue der Spalten mit mehreren Werten: Feld merken, Spalte in der Tabelle sortieren
    sortGroup(group, field, ascending) {
      this.groupSort[group] = field;
      this.$refs.table.setOrder(group, ascending);
    },
    debounceApplyFilters: debounce(function () {
      this.applyFilters({});
    }, 300),
    applyFilters(filters) {
      this.$refs.table.options.params = {
        ...this.$refs.table.options.params,
        ...filters,
        pageIndex: 0,
      };
      this.currentPage = 1;
      this.$refs.table.setPage(this.currentPage);
    },
    toggleCart(row, checked) {
      if (checked) {
        // Kopie ablegen: die Tabellenzeile wird beim naechsten Laden neu aufgebaut
        this.$set(this.selected, row.productId, { ...row, orderQuantity: row.orderQuantity });
      } else {
        this.$delete(this.selected, row.productId);
      }
    },
    removeFromCart(item) {
      this.$delete(this.selected, item.productId);
      const row = this.tableData.find((r) => r.productId === item.productId);
      if (row) row.checked = false;
    },
    clearCart() {
      this.selected = {};
      this.tableData.forEach((r) => {
        r.checked = false;
      });
    },
    updateCartQuantity(item, value) {
      const qty = value === "" ? null : Number(value);
      this.$set(this.selected, item.productId, { ...this.selected[item.productId], orderQuantity: qty });
      const row = this.tableData.find((r) => r.productId === item.productId);
      if (row) row.orderQuantity = qty;
    },
    // Mengenaenderung in der Tabelle in den Warenkorb spiegeln (dort liegt eine Kopie der Zeile)
    syncRowQuantity(row) {
      if (!this.selected[row.productId]) return;
      this.$set(this.selected, row.productId, { ...this.selected[row.productId], orderQuantity: row.orderQuantity });
    },
    // Position ohne gemeinsamen Lieferanten mit der ersten Position (nur wenn es keine Schnittmenge gibt)
    isForeignSupplier(item) {
      if (this.commonSuppliers.length > 0) return false;
      const first = this.selectedItems[0];
      if (!first || first.productId === item.productId) return false;
      return !(item.suppliers || []).some((s) => (first.suppliers || []).some((x) => x.id === s.id));
    },
    async loadStock(sku) {
      if (!sku || this.stockLoading[sku] || this.stockDetails[sku]) return;
      this.$set(this.stockLoading, sku, true);
      try {
        const rows = await this.$store.dispatch("warehouses/getWarehouseInventoriesBySku", { sku });
        this.$set(this.stockDetails, sku, Array.isArray(rows) ? rows : []);
      } catch (e) {
        this.$set(this.stockDetails, sku, []);
      } finally {
        this.$set(this.stockLoading, sku, false);
      }
    },
    stockRows(row) {
      return (this.stockDetails[row.sku] || []).map((i, index) => ({
        key: `${i.id || i.storageLocationId || index}`,
        storageLocation: i.inventoryName,
        quantity: i.inventory ?? 0,
        goodsReceiptDate: i.date || null,
      }));
    },
    loadInflow(row) {
      if (!row.inFlow || this.inflows[row.productId]) return;
      axiosFactory()
        .get(`/shipping_data/products/${row.productId}/inflow`)
        .then((res) => this.$set(this.inflows, row.productId, (res.data && res.data.data) || []))
        .catch(() => this.$set(this.inflows, row.productId, []));
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString(this.$i18n.locale);
    },
    salesTooltip(row, days) {
      const lager = row["sales" + days] || 0;
      const fba = row["fba" + days] || 0;
      return `${this.$t("procurement.salesSplit.warehouse")}: ${lager} · FBA: ${fba} · ${this.$t("procurement.salesSplit.total")}: ${lager + fba}`;
    },
    formatRange(value) {
      if (value == null) return "–";
      return this.$options.filters.formatUnit(value, this.$t("procurement.months"), 1, 1);
    },
    calcCbm(row) {
      const qty = row.orderQuantity;
      if (!qty || !row.shippingSizeLength || !row.shippingSizeWidth || !row.shippingSizeHeight) return null;
      return Math.round((qty * row.shippingSizeLength * row.shippingSizeWidth * row.shippingSizeHeight) / 10000) / 100;
    },
    // VK ist brutto; netto = VK / (1 + VAT), VAT aus den Allgemeinen Kosten (wie in der Angebotskalkulation)
    nettoVk(row) {
      if (row.currentSellingPrice == null) return null;
      return row.currentSellingPrice / (1 + (row.vatPercent != null ? row.vatPercent : 20) / 100);
    },
    marginSign(value) {
      if (value == null) return "";
      return value >= 0 ? "marge-positive" : "marge-negative";
    },
    fmtUsd(value) {
      return value == null ? "–" : this.$options.filters.formatUnit(value, "USD", 2, 2);
    },
    fmtEur(value) {
      return value == null ? "–" : this.$options.filters.formatUnit(value, "€", 2, 2);
    },
    // "15,68 € / 62,8 %" oder "–" ohne Wert
    eurPct(eur, pct) {
      if (eur == null && pct == null) return "–";
      return `${this.fmtEur(eur)} / ${pct == null ? "–" : this.$options.filters.formatUnit(pct, "%", 1, 1)}`;
    },
    // Rohgewinn: gespeicherter Wert der Angebotskalkulation, sonst Netto-VK minus BEP des Produkts
    grossProfitEur(row) {
      return row.grossProfit != null ? row.grossProfit : this.marginEur(row);
    },
    grossProfitPct(row) {
      return row.grossProfitPercent != null ? row.grossProfitPercent : this.marginPct(row);
    },
    saleGross(row) {
      return row.marginSaleGross != null ? row.marginSaleGross : row.currentSellingPrice;
    },
    saleNet(row) {
      return row.marginSaleNet != null ? row.marginSaleNet : this.nettoVk(row);
    },
    // Marge in EUR = Netto-VK - BEP
    marginEur(row) {
      const netto = this.nettoVk(row);
      if (netto == null || row.bep == null) return null;
      return netto - row.bep;
    },
    // Marge in % = Marge / Netto-VK * 100
    marginPct(row) {
      const netto = this.nettoVk(row);
      const marge = this.marginEur(row);
      if (netto == null || marge == null || netto === 0) return null;
      return (marge / netto) * 100;
    },
    sortSkuBy(field) {
      const table = this.$refs.table;
      const same = this.skuSortField === field && table.orderBy.column === "sku";
      // gleicher Link nochmal = Richtung drehen; sonst SKU aufsteigend bzw. ID absteigend (neueste oben)
      const ascending = same ? !table.orderBy.ascending : field === "sku";
      this.skuSortField = field;
      table.setOrder("sku", ascending);
    },
    filterBySupplier(supplier) {
      this.supplierFilter = { value: supplier.id, name: supplier.name };
      this.applyFilters({});
    },
    showChartModal(row) {
      this.chartRow = row;
      this.chartModal = true;
    },
    startOrder() {
      const items = this.selectedItems;
      if (items.length === 0) return;
      if (this.commonSuppliers.length === 0) {
        this.multiSupplierModal = true;
        return;
      }
      this.orderSupplierId = this.commonSuppliers[0].id;
      this.orderArrivalDate = this.defaultArrivalDate();
      this.orderCreatePlan = true;
      this.orderSendEmail = true;
      this.confirmModal = true;
    },
    // Vorschlag fuer das Anlieferdatum: heute + 12 Wochen, 08:00 Uhr
    defaultArrivalDate() {
      const d = new Date();
      d.setDate(d.getDate() + 84);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 8, 0, 0);
    },
    confirmOrder() {
      this.confirmModal = false;
      if (this.orderSendEmail) {
        this.prepareEmail();
        this.emailModal = true;
      } else {
        this.submitOrder();
      }
    },
    prepareEmail() {
      const items = this.selectedItems;
      const today = new Date();
      const dateStr = `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(2, "0")}.${today.getFullYear()}`;
      this.emailSubject = `Purchase Order CS Trading – ${dateStr}`;
      const contact = this.orderSupplier.contact;
      const salutation = contact ? `Dear ${contact},` : "Dear Sir or Madam,";
      const lines = items.map((i) => `${i.orderQuantity || 0} x ${i.sku}`);
      this.emailBody = `${salutation}

we would like to place the following order:

${lines.join("\n")}

Please confirm receipt of this order and let us know the expected production and delivery time.

Best regards
CS Trading GmbH & Co. KG`;
    },
    submitOrder() {
      const items = this.selectedItems;
      const invalid = items.filter((i) => !i.orderQuantity || i.orderQuantity <= 0);
      if (invalid.length > 0) {
        this.$notify({
          timeout: 3500,
          message: this.$t("procurement.order.invalidQuantities", { skus: invalid.map((i) => i.sku).join(", ") }),
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return;
      }
      const payload = {
        supplierId: this.orderSupplier.id,
        items: items.map((i) => ({ productId: i.productId, quantity: i.orderQuantity })),
        createShippingPlan: this.orderCreatePlan,
        arrivalDate: this.orderArrivalDate ? formatISO(this.orderArrivalDate) : null,
        sendEmail: this.orderSendEmail,
        emailSubject: this.emailSubject,
        emailBody: this.emailBody,
      };
      this.isLoading = true;
      axiosFactory()
        .post(`/procurement/order`, payload)
        .then((res) => {
          const result = res.data.data;
          if (result.error) {
            this.$notify({
              timeout: 4000,
              message: this.$t(`procurement.order.errors.${result.error}`),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "danger",
            });
            return;
          }
          this.emailModal = false;
          this.$notify({
            timeout: 3000,
            message: this.orderCreatePlan
              ? this.$t("procurement.order.successWithPlan", { supplier: this.orderSupplierName })
              : this.$t("procurement.order.success", { supplier: this.orderSupplierName }),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
          this.selected = {};
          this.$refs.table.refresh();
        })
        .catch(() => {
          this.$notify({
            timeout: 3500,
            message: this.$t("procurement.order.errors.request_failed"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  updated() {
    EventBus.$emit("scroll:changed");
  },
  created() {
    this.$store.dispatch("shippingData/getSupplierNames");
  },
};
</script>
<style lang="scss">
.table-procurement {
  td {
    font-size: 12px;
    padding: 4px 6px !important;
    vertical-align: middle;
  }
}

.cell-moq {
  display: flex;
  align-items: center;
  gap: 4px;

  .moq-eol-info {
    line-height: 1.3;
  }

  .eol-indicator {
    display: flex;
    align-items: center;
    gap: 2px;

    .md-icon {
      font-size: 16px !important;
      width: 16px;
      min-width: 16px;
      height: 16px;
    }
    .eol-yes {
      color: #ff9800 !important;
    }
    .eol-no {
      color: #7f915a !important;
    }
  }

  .eol-moq-edit {
    margin: 0;
    width: 26px;
    min-width: 26px;
    height: 26px;

    .md-icon {
      font-size: 16px !important;
    }
  }
}

.eolmoq-form {
  min-width: 260px;
  padding: 4px 2px;
}

.cell-prices {
  line-height: 1.35;
  white-space: nowrap;

  .marge-positive {
    color: #7f915a;
    font-weight: 600;
  }
  .marge-negative {
    color: #f44336;
    font-weight: 600;
  }
  .marge-warn {
    color: #ff9800;
    font-weight: 600;
  }
}

/* Sticky-Dock rechts (nach Lieferplan-Muster) */
.sp-dock {
  position: fixed;
  top: 110px;
  right: 0;
  z-index: 1031;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.sp-dock-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid #bbb;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
}
.sp-dock-trigger .md-icon {
  color: #fff !important;
}
.sp-dock-trigger--active {
  background: #7f915a;
}
.sp-dock-trigger--disabled {
  opacity: 0.45;
  cursor: default;
}
.sp-dock-badge {
  position: absolute;
  top: -4px;
  left: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: #7f915a;
  color: #fff;
  border-radius: 9px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.sp-dock-search {
  position: relative;
}
.sp-dock-search-panel {
  position: absolute;
  right: 44px;
  top: 0;
  width: 360px;
  background: #fff;
  padding: 20px 16px 16px;
  border-radius: 6px 0 0 6px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.25);
  overflow: visible;
}
.sp-dock-search-panel .md-field {
  margin-bottom: 8px;
}

.sp-dock-info.procurement-dock-info {
  position: fixed;
  bottom: 12px;
  right: 0;
  z-index: 1029;
  background: #fff;
  border: 1px solid #ddd;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  padding: 4px 12px 8px;
  font-size: 12px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 210px;
  max-width: 320px;
  overflow-x: hidden;
}
.sp-dock-info .sp-dock-info-toggle {
  position: absolute;
  top: 2px;
  right: 4px;
  cursor: pointer;
  line-height: 1;
  /* Der nachfolgende md-switch erstreckt sich ueber die volle Boxbreite und wuerde
     den Pfeil sonst ueberdecken und die Klicks abfangen */
  z-index: 3;
  padding: 2px;
}
.sp-dock-info .sp-dock-info-toggle .md-icon {
  font-size: 18px !important;
  color: #999;
}
.sp-dock-info.sp-dock-info--collapsed {
  min-width: 0;
  width: 44px;
  height: 44px;
  max-width: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sp-dock-info.sp-dock-info--collapsed .sp-dock-info-toggle {
  position: static;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.procurement-dock-info .cart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0 6px;
  border-bottom: 1px solid #eee;
}
.procurement-dock-info .cart-clear {
  color: #f44336;
  cursor: pointer;
}
.procurement-dock-info .cart-list {
  max-height: 220px;
  overflow-y: auto;
  overflow-x: hidden;
}
.procurement-dock-info .cart-row {
  display: grid;
  /* minmax(0, 1fr): ohne die 0-Untergrenze schiebt eine lange SKU die Zeile breiter
     als die Box und erzeugt einen Querscrollbalken. */
  grid-template-columns: minmax(0, 1fr) 52px 62px 18px;
  gap: 6px;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}
.procurement-dock-info .cart-row--foreign {
  background: #fff8e1;
}
.procurement-dock-info .cart-row--foreign .cart-sku,
.procurement-dock-info .cart-row--foreign .cart-cbm {
  color: #ef6c00;
}
.procurement-dock-info .cart-sku {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.procurement-dock-info .cart-warn-icon {
  font-size: 13px !important;
  color: #ef6c00 !important;
  vertical-align: middle;
}
.procurement-dock-info .cart-qty {
  width: 100%;
  height: 22px;
  font-size: 12px;
  text-align: right;
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 0 4px;
}
.procurement-dock-info .cart-cbm {
  text-align: right;
  color: #666;
}
.procurement-dock-info .cart-remove {
  cursor: pointer;
  line-height: 1;
}
.procurement-dock-info .cart-remove .md-icon {
  font-size: 15px !important;
  color: #999 !important;
}
.procurement-dock-info .cart-remove:hover .md-icon {
  color: #f44336 !important;
}
.procurement-dock-info .cart-hint {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  background: #fff8e1;
  color: #ef6c00;
  border-radius: 4px;
  padding: 4px 6px;
  margin: 6px 0;
}
.procurement-dock-info .cart-hint .md-icon {
  font-size: 14px !important;
  color: #ef6c00 !important;
}
.procurement-dock-info .cart-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-top: 1px solid #eee;
  margin-top: 6px;
  padding-top: 6px;
  color: #7f915a;
  cursor: pointer;
}
.procurement-dock-info .cart-toggle .md-icon {
  font-size: 16px !important;
  color: #7f915a !important;
}
.procurement-dock-info .md-switch {
  margin: 6px 0;
}
.procurement-dock-info .sp-dock-info-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 1px 0;
}
.procurement-filter-row {
  align-items: center;

  .supplier-filter {
    margin-top: 10px;
  }

  .order-button-wrap {
    text-align: right;
  }
}

.cell-idline {
  color: #777;
  font-size: 11px;
}
.sku-head a {
  cursor: pointer;
  color: inherit;
}
.sku-head a.active {
  font-weight: 700;
  text-decoration: underline;
}

.cell-grouped {
  line-height: 1.35;
}

.supplier-link {
  color: #7f915a !important;
  text-decoration: underline;
  cursor: pointer;
}

.cell-recommendation {
  .cbm-info {
    color: #999;
    font-size: 11px;
    white-space: nowrap;
  }

  .order-quantity-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .order-quantity-field {
    width: 70px;
    min-height: unset;
    margin: 0;
    padding-top: 0;

    input {
      font-size: 12px;
      height: 24px;
    }
  }
}

.row-select-checkbox {
  margin: 0;
}

.modal-container.procurement-chart-modal {
  width: min(960px, 95vw) !important;
  max-width: min(960px, 95vw) !important;
}

.procurement-chart-wrap {
  overflow-x: auto;
}

.big-chart {
  display: block;
  margin: 0 auto;

  .bar-value {
    font-size: 12px;
    fill: #333;
  }

  .bar-label {
    font-size: 11px;
    fill: #777;
  }
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 16px;
  font-size: 12px;
  color: #555;

  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
  }
}

.modal-container.procurement-email-modal {
  width: min(700px, 95vw) !important;
  max-width: min(700px, 95vw) !important;
}

.email-body-input {
  min-height: 260px !important;
  font-family: inherit;
}

.order-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-date {
  margin-top: 8px;
}

.order-date .order-date-title {
  margin-bottom: 4px;
  font-size: 12px;
  color: #aaa;
}
.cell-pricemargin .pm-table {
  border-collapse: collapse;
  font-size: 12px;
}
.cell-pricemargin .pm-table td {
  padding: 1px 0;
  border: 0;
  text-align: left;
  white-space: nowrap;
  vertical-align: middle;
}
.cell-pricemargin .pm-label {
  color: #777;
  padding-right: 10px;
}
.cell-pricemargin .pm-info {
  display: inline-block;
  vertical-align: middle;
  margin-left: 4px;
  color: #aaa;
  cursor: help;
}
.cell-pricemargin .pm-info .md-icon {
  font-size: 16px !important;
  width: 16px;
  min-width: 16px;
  height: 16px;
}
.cell-pricemargin .pm-info:hover {
  color: #7f915a;
}
.pm-tooltip-head {
  font-size: 11px;
  color: #777;
  margin-bottom: 4px;
  white-space: pre-line;
}
.md-tooltip.pm-tooltip {
  z-index: 99999 !important;
  height: auto;
  max-width: none;
  padding: 8px 10px;
}
</style>
